<!-- Modern Professional Sidebar Component -->
<aside
  class="new-sidebar"
  [class.collapsed]="isCollapsed()"
  [class.sticky-pin]="scrolled"
  id="new-sidebar"
>
  <!-- Company Brand -->
  <div class="sidebar-header">
    <a routerLink="/dashboard" class="brand-link">
      @if (companyLogo) {
        <img
          [src]="companyLogo"
          [alt]="companyName"
          class="brand-logo"
          [class.brand-logo-collapsed]="isCollapsed()"
          (error)="onCompanyLogoError($event)"
        />
      } @else {
        @if (isCollapsed()) {
          <span class="brand-initial">{{ companyInitial }}</span>
        } @else {
          <span class="brand-name" [style.font-size]="brandFontSize">{{ companyName || 'Dashboard' }}</span>
        }
      }
    </a>
  </div>

  <!-- Error Banner -->
  @if (modulesError) {
    <div class="sidebar-error">
      <i class="ri-error-warning-line"></i>
      <span>Failed to load menu</span>
      <button class="retry-btn" (click)="refreshModules()">
        <i class="ri-refresh-line"></i>
      </button>
    </div>
  }

  <!-- Loading State -->
  @if (isLoadingModules) {
    <div class="sidebar-loading">
      <div class="loading-skeleton" *ngFor="let i of [1,2,3,4,5]">
        <div class="skeleton-icon"></div>
        <div class="skeleton-text" [class.hidden]="isCollapsed()"></div>
      </div>
    </div>
  }

  <!-- Main Navigation -->
  <nav class="sidebar-nav" *ngIf="!isLoadingModules">
    <overlay-scrollbars [defer] class="sidebar-scroll">
      <ul class="nav-menu">
        @for (menuItem of menuItems; track menuItem.title) {
          <!-- Category Header -->
          @if (menuItem.headTitle) {
            <li class="nav-category" [class.collapsed]="isCollapsed()">
              @if (!isCollapsed()) {
                <span class="category-name">{{ menuItem.headTitle }}</span>
              } @else {
                <span class="category-divider"></span>
              }
            </li>
          }

          <!-- Menu Item -->
          @if (menuItem.title) {
            <li
              class="nav-item"
              [class.active]="menuItem.selected"
              [class.has-sub]="menuItem.type === 'sub'"
              [class.open]="menuItem.active"
            >
              <!-- Direct Link -->
              @if (menuItem.type === 'link') {
                <a
                  class="nav-link"
                  [routerLink]="[menuItem.path]"
                  routerLinkActive="active"
                  (click)="onMenuItemClick($event, menuItem)"
                  [title]="isCollapsed() ? menuItem.title : ''"
                >
                  <span class="nav-icon">
                    @if (menuItem.icon) {
                      <i class="bx" [ngClass]="menuItem.icon"></i>
                    }
                  </span>
                  <span class="nav-label" [class.hidden]="isCollapsed()">
                    {{ menuItem.title }}
                  </span>
                  @if (menuItem.badgeValue && !isCollapsed()) {
                    <span class="nav-badge" [ngClass]="'bg-' + menuItem.badgeClass">
                      {{ menuItem.badgeValue }}
                    </span>
                  }
                </a>
              }

              <!-- Submenu Parent -->
              @if (menuItem.type === 'sub') {
                <a
                  class="nav-link"
                  href="javascript:void(0)"
                  (click)="toggleSubmenu($event, menuItem)"
                  [title]="isCollapsed() ? menuItem.title : ''"
                >
                  <span class="nav-icon">
                    @if (menuItem.icon) {
                      <i class="bx" [ngClass]="menuItem.icon"></i>
                    }
                  </span>
                  <span class="nav-label" [class.hidden]="isCollapsed()">
                    {{ menuItem.title }}
                  </span>
                  @if (menuItem.badgeValue && !isCollapsed()) {
                    <span class="nav-badge" [ngClass]="'bg-' + menuItem.badgeClass">
                      {{ menuItem.badgeValue }}
                    </span>
                  }
                  <i
                    class="nav-arrow ri-arrow-right-s-line"
                    [class.hidden]="isCollapsed()"
                    [class.rotated]="menuItem.active"
                  ></i>
                </a>

                <!-- Submenu Items -->
                @if (menuItem.children && menuItem.children.length > 0) {
                  <ul
                    class="submenu"
                    [class.open]="menuItem.active"
                    [@submenuAnimation]="menuItem.active ? 'open' : 'closed'"
                  >
                    @for (child of menuItem.children; track child.title) {
                      <li
                        class="submenu-item"
                        [class.active]="child.selected"
                        [class.has-sub]="child.type === 'sub'"
                        [class.open]="child.active"
                      >
                        @if (child.type === 'link') {
                          <a
                            class="submenu-link"
                            [routerLink]="[child.path]"
                            routerLinkActive="active"
                            (click)="onMenuItemClick($event, child)"
                          >
                            <span class="submenu-dot"></span>
                            <span class="submenu-label">{{ child.title }}</span>
                          </a>
                        }

                        @if (child.type === 'sub') {
                          <a
                            class="submenu-link"
                            href="javascript:void(0)"
                            (click)="toggleSubmenu($event, child)"
                          >
                            <span class="submenu-dot"></span>
                            <span class="submenu-label">{{ child.title }}</span>
                            <i
                              class="submenu-arrow ri-arrow-right-s-line"
                              [class.rotated]="child.active"
                            ></i>
                          </a>

                          <!-- Nested Submenu -->
                          @if (child.children && child.children.length > 0) {
                            <ul
                              class="submenu nested"
                              [class.open]="child.active"
                              [@submenuAnimation]="child.active ? 'open' : 'closed'"
                            >
                              @for (nested of child.children; track nested.title) {
                                <li
                                  class="submenu-item"
                                  [class.active]="nested.selected"
                                >
                                  <a
                                    class="submenu-link"
                                    [routerLink]="[nested.path]"
                                    routerLinkActive="active"
                                    (click)="onMenuItemClick($event, nested)"
                                  >
                                    <span class="submenu-dot"></span>
                                    <span class="submenu-label">{{ nested.title }}</span>
                                  </a>
                                </li>
                              }
                            </ul>
                          }
                        }
                      </li>
                    }
                  </ul>
                }
              }

              <!-- External Link -->
              @if (menuItem.type === 'external') {
                <a
                  class="nav-link"
                  [href]="menuItem.path"
                  target="_blank"
                  rel="noopener noreferrer"
                  [title]="isCollapsed() ? menuItem.title : ''"
                >
                  <span class="nav-icon">
                    @if (menuItem.icon) {
                      <i class="bx" [ngClass]="menuItem.icon"></i>
                    }
                  </span>
                  <span class="nav-label" [class.hidden]="isCollapsed()">
                    {{ menuItem.title }}
                  </span>
                  <i class="ri-external-link-line external-icon" [class.hidden]="isCollapsed()"></i>
                </a>
              }
            </li>
          }
        }
      </ul>
    </overlay-scrollbars>
  </nav>

  <!-- Sidebar Footer - User Info -->
  <div class="sidebar-footer" [class.collapsed]="isCollapsed()">
    <div class="user-section" ngbDropdown placement="top-start">
      <button
        class="user-trigger"
        type="button"
        ngbDropdownToggle
        id="sidebarUserDropdown"
      >
        <div class="user-avatar-wrapper">
          @if (hasProfileImage) {
            <img
              [src]="userData?.profileImage"
              alt="Profile"
              class="user-avatar-img"
              (error)="onImageError($event)"
            />
          } @else {
            <div class="user-avatar">
              <span>{{ getUserInitials() }}</span>
            </div>
          }
        </div>
        @if (!isCollapsed()) {
          <div class="user-info">
            <span class="user-name">{{ userData?.name || 'User' }}</span>
            <span class="user-role">{{ userData?.roleName || '' }}</span>
          </div>
          <svg class="user-more-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="5" r="1"></circle>
            <circle cx="12" cy="12" r="1"></circle>
            <circle cx="12" cy="19" r="1"></circle>
          </svg>
        }
      </button>

      <div ngbDropdownMenu class="dropdown-menu user-footer-dropdown" aria-labelledby="sidebarUserDropdown">
        <div class="dropdown-user-header">
          <div class="dropdown-avatar-wrapper">
            @if (hasProfileImage) {
              <img [src]="userData?.profileImage" alt="Profile" class="dropdown-avatar-img" />
            } @else {
              <div class="dropdown-avatar">
                <span>{{ getUserInitials() }}</span>
              </div>
            }
          </div>
          <span class="dropdown-user-name">{{ userData?.name }}</span>
          <span class="dropdown-user-role">{{ userData?.roleName }}</span>
        </div>
        <div class="dropdown-divider"></div>
        <button class="dropdown-item" (click)="openAccountDialog()" type="button">
          <div class="item-icon-wrap">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"></path>
              <circle cx="12" cy="7" r="4"></circle>
            </svg>
          </div>
          <span>My Profile</span>
        </button>
        <div class="dropdown-divider"></div>
        <button class="dropdown-item logout-item" (click)="onLogout()" type="button">
          <div class="item-icon-wrap logout-icon-wrap">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
              <polyline points="16 17 21 12 16 7"></polyline>
              <line x1="21" y1="12" x2="9" y2="12"></line>
            </svg>
          </div>
          <span>Sign Out</span>
        </button>
      </div>
    </div>
  </div>
</aside>
